/**
 * The server's clock, sampled from the `Date` response header and carried forward on a monotonic timer.
 *
 * Every deadline this app cares about — token expiry above all — is stamped by the server and enforced by the
 * server, so the device clock is not a second opinion about them, it is noise. A device whose clock is hours
 * fast reads a token it was handed seconds ago as long expired; one whose clock is hours slow keeps using a
 * token the server has already stopped honouring. Neither is detectable from the token alone: `exp` in the past
 * looks identical whether the device is wrong or the server issued something stale.
 *
 * The `Date` header settles it. It is the server saying what time it is, and reading it at the moment of receipt
 * gives the skew directly — magnitude and sign.
 *
 * WHOSE `Date`, though, is the whole question. "The server" here is not any server: it is the one that stamped
 * the `exp` this clock gets compared against, and only that machine's time cancels the skew rather than adding
 * another. `fetchShim` fans out across origins this app does not control — a CDN profile in `sendRequest` swaps
 * the base URL outright — and folding one of those `Date`s in would write a second, unrelated skew into the same
 * number token expiry is read off. So sampling is OPT-IN per request (`trustServerClock` on `FetchOptions`), set
 * only by the calls that mint or rotate the token: `authentication/v1`'s initialize/signIn/signout/refresh, and
 * the legacy `auth/` ticket exchange in `services.ts`. Every one of them returns a bearer token, so the sample
 * always lands from the same response that carries the `exp` it will be measured against — and lands just before
 * `_onTokenReceived` reads it.
 *
 * Not every trusted `Date` is that server speaking NOW, either: a cached response replays the one it was stored
 * with. Trusting one of those would invent skew rather than cancel it, and in the dangerous direction — a `Date`
 * from the past makes tokens look longer-lived than they are, so the refresh is armed late and requests go out
 * against a token the server has already retired. Screening it takes both ends. `sampleServerClock` rejects what it
 * can recognize — an `Age` a shared cache added, and a reading behind what the previous sample already implies — but
 * the FIRST sample has nothing to be compared against, and no reading available here can fix that: a `Date` two
 * hours behind the device clock is indistinguishable from the legitimate case of a device two hours fast, which is
 * the case this module exists to serve. So `fetchShim` sends every clock-sampled request `cache: 'no-store'`, and a
 * response that could not come from a cache is what makes trusting that first sample sound.
 *
 * Two clocks are read here, for different jobs:
 *
 *  - `performance.now()` is monotonic. Stepping the wall clock — NTP correcting it, someone fixing the date by
 *    hand — cannot move it, so it is what carries a sample forward between requests.
 *  - `Date.now()` is read only as a cross-check. On some platforms the monotonic timer does not advance while
 *    the device is suspended, and the wall clock does; taking the LARGER of the two elapsed readings covers
 *    that without trusting either on its own.
 *
 * Taking the larger is deliberate and always the safe direction: over-stating how much time has passed costs
 * an early token refresh, under-stating it means using a token the server has already retired.
 */

/** Server time at the most recent sample, epoch ms. Zero until the first sample lands. */
let _sampleServerTimeMs = 0;
/** `performance.now()` when that sample was taken. */
let _sampleMonotonicMs = 0;
/** `Date.now()` when that sample was taken. */
let _sampleWallMs = 0;

/** One instant, read off both clocks at once, so `elapsedSince` can later cross-check them against each other. */
interface ClockMarks {
    monotonic: number;
    wall: number;
}

function monotonicNow() {
    return performance.now();
}

function takeClockMarks(): ClockMarks {
    return { monotonic: monotonicNow(), wall: Date.now() };
}

/**
 * How much time has passed since a pair of marks taken by `takeClockMarks`, in ms.
 *
 * Never negative and never an under-estimate: the monotonic reading survives the wall clock being stepped in
 * either direction, the wall reading survives a monotonic timer frozen by device suspend, and the larger of
 * the two is the one that cannot quietly hand back a dead token.
 */
function elapsedSince({ monotonic, wall }: ClockMarks) {
    const monotonicElapsed = monotonicNow() - monotonic;
    const wallElapsed = Date.now() - wall;
    return Math.max(monotonicElapsed, wallElapsed > 0 ? wallElapsed : 0);
}

/** Whether a server `Date` has ever been observed. Until it has, `serverNow` is just the device clock. */
function hasServerClock() {
    return _sampleServerTimeMs > 0;
}

/**
 * Best estimate of the time on the server right now, epoch ms.
 *
 * Falls back to the device clock before the first sample — which is the boot window, when the auth token may
 * come from the `Index.cshtml` preload rather than a response this module can see. The first real request
 * corrects it.
 */
function serverNow() {
    if (!hasServerClock()) {
        return Date.now();
    }
    return _sampleServerTimeMs + elapsedSince({ monotonic: _sampleMonotonicMs, wall: _sampleWallMs });
}

/**
 * How far behind its predecessor a sample may sit before it is treated as stale rather than as the server's
 * clock. Covers the one-second resolution of an HTTP-date, the spread between load-balanced origins and latency
 * jitter — all of which are seconds. A replayed cache hit is minutes or hours out, so it clears this easily.
 */
const ALLOWED_REGRESSION_MS = 5 * 1000;

/**
 * Whether a reading is too far in the past to be the server's current time.
 *
 * Real server time only moves forward, so a `Date` that sits behind what the previous sample already implies
 * did not come from the origin just now — it was replayed from a cache. This is the check that actually catches
 * that, because `Age` does not: shared caches add it, but a BROWSER cache hit replays the stored headers,
 * original `Date` and all, and adds nothing.
 *
 * It answers `false` for the FIRST sample, and has to: with nothing to compare against there is no reading here
 * that could screen one. The device clock cannot stand in — a `Date` two hours behind it is exactly what a device
 * two hours fast legitimately produces, which is the case this module exists to serve, so any device-relative floor
 * would reject the samples it most needs. That gap is closed at the other end instead: `fetchShim` sends every
 * clock-sampled request with `cache: 'no-store'`, so a sampled response cannot be a cache hit in the first place.
 * If that ever changes, this function is where the consequence lands.
 *
 * The elapsed term is the MONOTONIC reading only, deliberately not `elapsedSince`. That helper returns the
 * larger of the monotonic and wall readings, so a device whose wall clock jumps forward inflates the estimate —
 * and an inflated estimate used here would reject every correct sample that could bring it back, wedging the
 * module on a bad reading for the rest of the session. The monotonic reading is a lower bound on real elapsed
 * time, which is what a staleness floor wants: it errs toward accepting, and accepting a fresh sample is always
 * safe.
 */
function isStaleSample(serverTimeMs: number) {
    if (!hasServerClock()) {
        return false;
    }
    const impliedServerTime = _sampleServerTimeMs + (monotonicNow() - _sampleMonotonicMs);
    return serverTimeMs < impliedServerTime - ALLOWED_REGRESSION_MS;
}

/**
 * Records the server clock from one response's headers.
 *
 * The caller vouches for the source: `fetchShim` calls this only for a request that opted in with
 * `trustServerClock`, which is the token-issuing services and nothing else. Nothing here can tell whose clock a
 * `Date` describes, so widening who calls it widens what can move token expiry — see the module comment.
 * Called for such a response whether it succeeded or not: an error response carries just as good a `Date`, and a
 * token storm is exactly when the reading is most wanted.
 *
 * Three guards keep a cached response from teaching this module the wrong time, which is the failure that matters
 * most: a `Date` from the past makes tokens look longer-lived than they are, so the refresh is armed late and
 * requests go out against a token the server has already retired. `Age` catches shared caches, on the first sample
 * as well as later ones; `isStaleSample` catches the browser's own, and anything else that replays an old `Date`,
 * from the second sample on; and `cache: 'no-store'` at the `fetchShim` end keeps a cache hit from reaching the
 * first sample at all, which is the one `isStaleSample` structurally cannot judge.
 *
 * Network latency is not corrected for. The header is written when the response is sent, so this runs about
 * half a round trip late — tens to hundreds of ms, well under the one-second resolution an HTTP-date has in the
 * first place, and three orders of magnitude under the refresh lead it feeds.
 */
function sampleServerClock(dateHeader: string | null, ageHeader?: string | null) {
    if (!dateHeader || (ageHeader && Number(ageHeader) > 0)) {
        return;
    }
    const serverTimeMs = Date.parse(dateHeader);
    if (!Number.isFinite(serverTimeMs) || isStaleSample(serverTimeMs)) {
        return;
    }
    _sampleServerTimeMs = serverTimeMs;
    _sampleMonotonicMs = monotonicNow();
    _sampleWallMs = Date.now();
}

export type { ClockMarks };
export { elapsedSince, hasServerClock, sampleServerClock, serverNow, takeClockMarks };
